/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  InjectionToken,
  Input,
  input,
  inject,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { OptionHoverService } from '../option-hover.service';
import { Option } from '../option/option';
import { OptionSelectionService } from '../selection/option-selection.service';
import { OptionOpenService } from '@ng-falcon/controls/option/option-open.service';

/**
 * Default for OptionsOverlayComponent.fitTriggerWidth. Provide `true` on a component to lock every
 * options panel rendered inside it to its trigger's width; leave it unprovided to let panels grow.
 */
export const NGF_OPTIONS_FIT_TRIGGER_WIDTH = new InjectionToken<boolean>('NgfOptionsFitTriggerWidth', {
  providedIn: 'root',
  factory: () => false
});

@Component({
    selector: 'ngf-options-overlay',
    templateUrl: './options-overlay.component.html',
    styleUrls: ['./options-overlay.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class OptionsOverlayComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input()
  set triggerRef(triggerRef: ElementRef<HTMLElement> | HTMLElement) {
    if (triggerRef instanceof ElementRef) {
      this._triggerRef = triggerRef;
    } else {
      this._triggerRef = new ElementRef(triggerRef);
    }
  }

  get triggerRef(): ElementRef<HTMLElement> {
    return this._triggerRef;
  }

  readonly disabled = input(false);
  readonly readonly = input(false);
  // False (default): the panel is at least as wide as its trigger and grows to its widest option.
  // True: the panel is exactly as wide as its trigger and long option labels ellipsize.
  readonly fitTriggerWidth = input(inject(NGF_OPTIONS_FIT_TRIGGER_WIDTH));

  listbox: ElementRef<HTMLElement>;

  private overlay: Overlay = inject(Overlay);
  private service: OptionSelectionService = inject(OptionSelectionService);
  private hoverService: OptionHoverService = inject(OptionHoverService);
  private openService: OptionOpenService = inject(OptionOpenService);
  private breakpointObserver: BreakpointObserver = inject(BreakpointObserver);

  private sub = new Subscription();
  private listboxKeySub = Subscription.EMPTY;
  private activeDescendantSub = Subscription.EMPTY;
  private scrollSub = Subscription.EMPTY;
  private repositionQueued = false;
  private _triggerRef: ElementRef<HTMLElement>;

  @ViewChild(CdkConnectedOverlay, { static: true })
  private connectedOverlay: CdkConnectedOverlay;

  // Template-bound state is held in signals so view updates happen automatically under OnPush
  // without needing ChangeDetectorRef.markForCheck() at every mutation site. Public getters
  // preserve the existing property-style read API for external consumers (e.g.
  // `select.optionsOverlay.isOpen` in templates and TS).
  private readonly _isOpen = signal(false);
  get isOpen(): boolean { return this._isOpen(); }

  private readonly _isPositioned = signal(false);
  get isPositioned(): boolean { return this._isPositioned(); }

  private readonly _triggerRect = signal<DOMRect | null>(null);
  get triggerRect(): DOMRect | null { return this._triggerRect(); }

  scrollStrategy: ScrollStrategy;
  positions: ConnectedPosition[];

  ngOnInit(): void {
    this._triggerRect.set(this._triggerRef.nativeElement.getBoundingClientRect());
    this.scrollStrategy = this.overlay.scrollStrategies.reposition();
    this.positions = [
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
      },
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom'
      },
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top'
      },
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom'
      }
    ];
  }

  ngAfterViewInit(): void {
    this.sub.add(
      this.handleKeydown$().subscribe()
    );
    this.sub.add(
      this.openService.focusListbox$.pipe(
        tap(() => this.prepareSearchListboxForFocus())
      ).subscribe()
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.listboxKeySub.unsubscribe();
    this.activeDescendantSub.unsubscribe();
    this.scrollSub.unsubscribe();
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open(true);
  }

  open(shouldFocus: boolean = false) {
    if (!(this.disabled() || this.readonly()) && !this.isOpen) {

      this._triggerRect.set(this._triggerRef.nativeElement.getBoundingClientRect());
      this._isPositioned.set(false);
      this.changeIsOpen(true);
      this.subscribeToScroll();

      // The listbox only exists in the DOM once the overlay has rendered, so wire it on every open
      // (typed or focused) to keep aria-activedescendant in sync with the hovered option.
      setTimeout(() => {
        if (this.isOpen) {
          this.prepareListboxForFocus();
        }
        // Hover first option if nothing is currently hovered (fixes missing focus on open).
        // Guard prevents a double-advance when callers also call hoverService.next() synchronously
        // before this setTimeout fires (e.g. openOrHoverNext, Enter/Space key handlers).
        if (shouldFocus && !this.hoverService.getValue()) {
          this.hoverService.next();
        }
      });
    }
  }

  onPositionChange(): void {
    if (!this.isPositioned) {
      this._isPositioned.set(true);
    }
  }

  private prepareSearchListboxForFocus() {
    // get element with the id on the options container(with the search box)
    const listboxes = document.querySelectorAll('[id="search-options-listbox"]');
    const listboxElement = listboxes[listboxes.length - 1] as HTMLElement;
    if (listboxElement) {
      // select listbox element and link keydown handling to existing handler
      this.listbox = new ElementRef(<HTMLElement>listboxElement);
      this.listboxKeySub.unsubscribe();
      this.activeDescendantSub.unsubscribe();
      this.listboxKeySub = merge(
        fromEvent(this.listbox.nativeElement, 'keydown').pipe(
          tap((event: KeyboardEvent) => this.handleKeydown(event))
        ),
        // add aria-activedescendant to listbox based on hovered option for screen reader
        this.hoverService.hovered$.pipe(
          tap(hovered => {
            const id = hovered?.elementRef.nativeElement.querySelector('[role="option"]')?.id ?? null;
            if (id) {
              listboxElement.setAttribute('aria-activedescendant', id);
            } else {
              listboxElement.removeAttribute('aria-activedescendant');
            }
          })
        )
      ).subscribe();
      // remove any previous hover(if options are filtered and the OptionComponent instances are recreated, the previously hovered option instance may no longer exist, so we need to clear the hover state)
      this.hoverService.hover(null);
      this.hoverService.next();
    }
  }

  private prepareListboxForFocus() {
    const listboxes = document.querySelectorAll('[id="options-container"]');
    const listboxElement = listboxes[listboxes.length - 1] as HTMLElement;
    if (listboxElement) {
      this.listbox = new ElementRef(<HTMLElement>listboxElement);
      this.activeDescendantSub.unsubscribe();
      // WAI-ARIA combobox pattern: keep DOM focus on the trigger and communicate the
      // highlighted option via aria-activedescendant instead of shifting focus to the listbox.
      // Re-synced on listbox DOM changes too, since a re-render (e.g. filtering) can remove the
      // hovered option without a hover change.
      const observer = new MutationObserver(() => this.syncActiveDescendant());
      observer.observe(this.connectedOverlay?.overlayRef?.overlayElement ?? listboxElement, { childList: true, subtree: true });
      this.activeDescendantSub = this.hoverService.hovered$.pipe(
        tap(() => this.syncActiveDescendant())
      ).subscribe();
      this.activeDescendantSub.add(() => observer.disconnect());
    }
  }

  private syncActiveDescendant(): void {
    // Resolved per call: the trigger input is swapped when its value is cleared while open.
    const triggerInput = this.getTriggerFocusTarget();
    if (!triggerInput) {
      return;
    }
    const hovered = this.hoverService.getValue()?.elementRef.nativeElement;
    // A hovered option removed from the DOM must not stay referenced by aria-activedescendant, or stay hovered.
    if (hovered && !hovered.isConnected) {
      this.hoverService.hover(null);
    }
    const id = hovered?.isConnected ? hovered.querySelector('[role="option"]')?.id : null;
    if (id) {
      triggerInput.setAttribute('aria-activedescendant', id);
    } else {
      triggerInput.removeAttribute('aria-activedescendant');
    }
  }

  close() {
    if (this.isOpen) {
      this.changeIsOpen(false);
      this.scrollSub.unsubscribe();
      this.hoverService.hover(null);
      // Only restore focus to the trigger when focus is still inside this
      // component (Escape, option select, keyboard close). If the user clicked
      // somewhere else, focus has already moved there — don't yank it back to
      // the trigger; that would steal focus from whatever the user just clicked
      // (e.g. another dialog's input).
      const active = document.activeElement;
      const focusIsInside = !!active && (
        !!this._triggerRef?.nativeElement.contains(active)
        || !!this.listbox?.nativeElement?.contains(active)
      );
      if (focusIsInside) {
        const focusTarget = this.getTriggerFocusTarget();
        setTimeout(() => focusTarget?.focus({ preventScroll: true }));
      }
    }
  }

  /**
   * Close the overlay on Tab/ShiftTab and make sure the browser's native focus-order
   * traversal continues from the trigger's real position in the page.
   *
   * Real DOM focus may currently be inside the popup's overlay content (e.g. the search
   * input or listbox), which CDK renders in a pane appended near the end of `<body>` — not
   * near the trigger's actual position in the page. The browser's default Tab action runs
   * right after this synchronous handler returns and moves focus based on whatever is
   * `document.activeElement` at that point, so if we don't return focus to the trigger here
   * first, the browser computes "next focusable element" from the overlay's DOM position and
   * wraps focus to the very top of the page instead of continuing naturally after the trigger.
   */
  private handleTabAway(): void {
    if (!this.isOpen) {
      return;
    }
    this.changeIsOpen(false);
    this.scrollSub.unsubscribe();
    this.hoverService.hover(null);

    const active = document.activeElement as HTMLElement | null;
    const triggerEl = this._triggerRef?.nativeElement;
    const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement;
    const focusIsInOverlay = !!active && !!overlayEl && overlayEl.contains(active);
    if (focusIsInOverlay && active !== triggerEl) {
      this.getTriggerFocusTarget()?.focus({ preventScroll: true });
    }
  }

  private getTriggerFocusTarget(): HTMLElement | null {
    const el = this._triggerRef?.nativeElement;
    if (!el) return null;
    if (el.matches('button, input, a[href], [tabindex]')) return el;
    return el.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled])');
  }

  private changeIsOpen(value: boolean) {
    this._isOpen.set(value);
    this.openService.setOpened(value);
    if (!value) {
      this.activeDescendantSub.unsubscribe();
      this.getTriggerFocusTarget()?.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * React to page scrolls while the options overlay is open. What "react" means depends on the
   * viewport.
   *
   * On handset, dismiss. Keeping the overlay glued to its trigger there is wrong: the trigger
   * scrolls up underneath the sticky header and the overlay — rendered in a fixed-position,
   * high-z-index pane — follows it and covers the header.
   *
   * Everywhere else, reposition. Dismissing on desktop/tablet collapses the list on scrolls the
   * user never aimed at it (wheel/trackpad with the pointer elsewhere), and on the
   * scroll-into-view a WebDriver performs before interacting with an element, which breaks
   * automation outright.
   *
   * cdkConnectedOverlay's reposition scroll strategy only listens for scrolls on the window
   * and on CdkScrollable-registered containers. Two cases slip through:
   *   1. Scrolls inside arbitrary sub-containers (mouse wheel / trackpad on a list inside a
   *      div without cdkScrollable) — fire scroll events that don't bubble, so CDK misses them.
   *   2. iOS Safari touch and momentum scrolling — scroll events are suppressed mid-gesture.
   * Catch (1) via a document capture-phase scroll listener (scroll doesn't bubble, but capture
   * sees every scrollable element) and (2) via touchmove.
   */
  private subscribeToScroll() {
    this.scrollSub.unsubscribe();
    this.scrollSub = merge(
      fromEvent(document, 'scroll', { passive: true, capture: true }),
      fromEvent(document, 'touchmove', { passive: true })
    ).subscribe((event: Event) => this.handleScroll(event));
  }

  private handleScroll(event: Event): void {
    const overlayRef = this.connectedOverlay?.overlayRef;
    if (!overlayRef) {
      return;
    }

    if (!this.isHandset()) {
      this.queueReposition();
      return;
    }

    // Scrolls/touches originating inside the overlay itself are the user working the list,
    // not leaving it — a long option list must stay scrollable.
    if (overlayRef.overlayElement?.contains(event.target as Node)) {
      return;
    }

    // The overlay detach restores focus to the trigger, which scrolls the trigger back into
    // view and jumps the page. For a scroll event we know the scrolled container and its
    // offset, so preserve it across the close.
    const scrollEl = event.type === 'scroll' ? this.getScrollElement(event.target) : null;
    const scrollTop = scrollEl?.scrollTop;
    this.close();
    if (scrollEl) {
      requestAnimationFrame(() => {
        scrollEl.scrollTop = scrollTop ?? scrollEl.scrollTop;
      });
    }
  }

  /**
   * A real handset: a small viewport *and* a touch-primary pointer.
   *
   * The viewport test alone is not enough. `Breakpoints.Handset` matches any landscape window up
   * to 959.98px wide, so a desktop browser in a half-screen window — or a WebDriver session,
   * whose default window is 800x600 — reads as a handset and loses its list on every scroll.
   * That is exactly the FLO-53992 regression this gate exists to prevent. A desktop pointer stays
   * `fine` however narrow the window gets, so requiring `coarse` confines the dismissal to the
   * devices the sticky-header overlap actually affects.
   */
  private isHandset(): boolean {
    return this.breakpointObserver.isMatched(Breakpoints.Handset)
      && this.breakpointObserver.isMatched('(pointer: coarse)');
  }

  /**
   * The element a scroll event actually moved. A scroll of the page itself targets `document`,
   * which has no scrollTop of its own — the offset lives on document.scrollingElement.
   */
  private getScrollElement(target: EventTarget | null): HTMLElement | null {
    if (target === document) {
      return document.scrollingElement as HTMLElement | null;
    }
    return target instanceof HTMLElement ? target : null;
  }

  /** Coalesce repositioning to one reflow per frame; scroll events can outpace the compositor. */
  private queueReposition(): void {
    if (this.repositionQueued) {
      return;
    }
    this.repositionQueued = true;
    requestAnimationFrame(() => {
      this.repositionQueued = false;
      this.connectedOverlay?.overlayRef?.updatePosition();
    });
  }

  handleClose(event: MouseEvent): void {
    const originElement = this._triggerRef?.nativeElement;
    if (this.notIn(originElement, event.target as Element)) {
      this.close();
    }
  }

  private handleKeydown$() {
    return fromEvent(this._triggerRef.nativeElement, 'keydown').pipe(
      tap((event: KeyboardEvent) => this.handleKeydown(event))
    );
  }

  private handleKeydown(event: KeyboardEvent): void {
    // If the event originated from a button or link inside the trigger (e.g. a pill's remove button),
    // don't intercept — let the element handle the event natively.
    const target = event.target as HTMLElement;
    if (target !== this._triggerRef?.nativeElement && target.matches('button, a[href], [role="button"]')) {
      return;
    }

    const keyMap: Record<string, string> = {
      Down: 'ArrowDown',
      Up: 'ArrowUp',
      Left: 'ArrowLeft',
      Right: 'ArrowRight',
      Esc: 'Escape',
    };

    let normalizedKey = keyMap[event.code] ?? event.code;
    // Distinguish Shift+Tab from Tab
    if (normalizedKey === 'Tab' && event.shiftKey) {
      normalizedKey = 'ShiftTab';
    }

    const keyActions: Record<string, () => void> = {
      ArrowDown: () => this.openOrHoverNext(),
      ArrowUp: () => this.hoverService.prev(),
      ArrowLeft: () => this.hoverService.left(),
      ArrowRight: () => this.hoverService.right(),
      Enter: () => {
        if (this.isOpen) {
          this.selectHovered();
        } else {
          this.open(true);
          this.hoverService.next();
        }
      },
      Escape: () => this.close(),
      Space: () => {
        if (!this.isOpen) {
          this.open(true);
          this.hoverService.next();
        } else if (this.hoverService.getValue()) {
          // Select the aria-activedescendant option while focus stays on the trigger or listbox.
          this.selectHovered();
        }
      },
      // Tab/ShiftTab: close the overlay and allow the browser's default focus movement to
      // continue naturally from the trigger.
      Tab: () => this.handleTabAway(),
      ShiftTab: () => this.handleTabAway(),
    };

    const wasOpen = this.isOpen;
    const fromListbox = !!this.listbox?.nativeElement.contains(target);
    const action = keyActions[normalizedKey];
    if (action) {
      action();
      if (normalizedKey === 'Escape' && wasOpen) {
        event.stopPropagation();
      }
      if (!this.shouldAllowDefault(normalizedKey, wasOpen, fromListbox)) {
        event.preventDefault();
      }
    }
  }

  private shouldAllowDefault(key: string, wasOpen: boolean, fromListbox: boolean): boolean {
    // Tab/ShiftTab: allow default so focus continues to next/prev element after trigger.
    if (key === 'Tab' || key === 'ShiftTab') {
      return true;
    }
    // Space: the trigger input is readonly, so its default is harmless. The search listbox is a
    // focused scroll container, though, and Space's default there pages it down — scrolling the
    // option just selected out of view (FLO-50622).
    return key === 'Space' && wasOpen && !fromListbox;
  }

  private openOrHoverNext() {
    if (!this.isOpen) {
      this.open(true);
    }
    this.hoverService.next();
  }

  private selectHovered() {
    const hovered = this.hoverService.getValue();

    // An option a filter has removed from the DOM can't be chosen any more.
    if (hovered && hovered instanceof Option && hovered.elementRef.nativeElement.isConnected) {
      this.service.select({ value: hovered.value, userInput: true });
    }
  }

  private notIn(el: HTMLElement, target: Element) {
    return el !== target && !el.contains(target);
  }
}
